/**
 * Check state for the Full response reader (spec 29 §0.6.4).
 *
 * One line that always answers "has this draft been checked, and is that
 * answer still true?" — plus the single action that resolves it.
 */

import { formatDistanceToNowStrict } from 'date-fns'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { reviewRunNotice } from '@/lib/review-run-notice'
import type { LatestCheck } from '@/lib/schemas'

export interface CheckStatusLineProps {
  /** null while the latest-check call is still in flight. */
  latest: LatestCheck | null
  issueCount: number
  isStarting: boolean
  onCheckAgain: () => void
  onRetry: () => void
}

function issueLabel(count: number): string {
  if (count === 0) return 'no issues'
  return count === 1 ? '1 issue' : `${count} issues`
}

/** "4 min ago" reads better than "4 minutes ago" in a 12px chrome line. */
function whenLabel(iso: string): string {
  return formatDistanceToNowStrict(new Date(iso), { addSuffix: true })
    .replace(' minutes', ' min')
    .replace(' minute', ' min')
}

export function CheckStatusLine({
  latest,
  issueCount,
  isStarting,
  onCheckAgain,
  onRetry,
}: CheckStatusLineProps) {
  if (!latest) return null

  const run = latest.run

  const action = (label: string, onClick: () => void) => (
    <Button variant="ghost" size="sm" disabled={isStarting} onClick={onClick}>
      {label}
    </Button>
  )

  let body: React.ReactNode
  if (!run) {
    body = (
      <>
        <span>Not checked yet</span>
        {action('Check now', onCheckAgain)}
      </>
    )
  } else if (run.status === 'running') {
    body = (
      <>
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        <span>Checking…</span>
      </>
    )
  } else if (run.status === 'failed') {
    body = (
      <>
        <span>Check failed</span>
        {action('Retry', onRetry)}
      </>
    )
  } else if (latest.stale) {
    body = (
      <>
        <span>Edited since last check</span>
        {action('Check again', onCheckAgain)}
      </>
    )
  } else {
    const at = run.completedAt ?? run.startedAt
    // A run that could not check every criterion must not read as "Checked ·
    // no issues". The full note sits in the Checks tab; the title carries it here.
    const notice = reviewRunNotice(run)
    body = (
      <>
        {notice ? (
          <span title={notice}>{`Partly checked ${whenLabel(at)} · ${issueLabel(issueCount)}`}</span>
        ) : (
          <span>{`Checked ${whenLabel(at)} · ${issueLabel(issueCount)}`}</span>
        )}
        {action('Check again', onCheckAgain)}
      </>
    )
  }

  return (
    <span data-testid="check-status-line" className="flex items-center gap-1.5 text-meta">
      {body}
    </span>
  )
}
